import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BoatViewer from './BoatViewer';
import ConfigPanel from './ConfigPanel';
import './Configurator.css';


const CATEGORIES = [
  { key: 'tops', label: 'Tops &\nTowers', icon: 'tops', iconSrc: '/Topstowers.webp' },
  { key: 'engines', label: 'Engines', icon: 'engines', iconSrc: '/Engines.webp' },
  { key: 'seating', label: 'Seating', icon: 'seating', iconSrc: '/Seating.webp' },
  { key: 'extras', label: 'Extras', icon: 'extras', iconSrc: '/vector-icon.webp' },
];

const CAMERA_VIEWS = [
  { key: 'exterior', label: 'Exterior' },
  { key: 'front', label: 'Front' },
  { key: 'top', label: 'Top' },
  { key: 'stern', label: 'Stern' },
];

// Price data matching ConfigPanel
const PRICE_DATA = {
  tops: { bimini_open: 950, bimini_folded: 950 },
  engines: { outboard: 4500 },
  seating: { fore_seat: 780, fore_sunbed: 750, aft_sunbed: 800 },
  extras: {
    foreCushion: 290, foreTableUp: 430, foreTableDown: 700,
    gps: 780, windshield: 950, multimedia: 780, sideRails: 780,
  },
};

const BASE_PRICE = 18500;

function Configurator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('tops');
  const [panelOpen, setPanelOpen] = useState(true);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  // Block devtools only on this page
  useEffect(() => {
    const blockKeys = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I','J','C'].includes(e.key)) || (e.metaKey && e.altKey && ['I','J'].includes(e.key))) {
        e.preventDefault(); e.stopPropagation();
      }
    };
    const blockMenu = (e) => e.preventDefault();
    document.addEventListener('keydown', blockKeys, true);
    document.addEventListener('contextmenu', blockMenu, true);
    return () => {
      document.removeEventListener('keydown', blockKeys, true);
      document.removeEventListener('contextmenu', blockMenu, true);
    };
  }, []);

  const [viewerHeight, setViewerHeight] = useState(window.innerHeight - 70);

  useEffect(() => {
    const update = () => setViewerHeight(window.innerHeight - 70);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const cameraVersion = useRef(0);
  const [cameraIntent, setCameraIntent] = useState({ type: 'category', key: 'tops', version: 0 });

  const moveCameraTo = useCallback((type, key) => {
    cameraVersion.current += 1;
    setCameraIntent({ type, key, version: cameraVersion.current });
  }, []);

  useEffect(() => {
    const handleBeforePrint = () => {
      const canvas = document.querySelector('.configurator__viewer-area canvas');
      const summary = document.querySelector('.configurator__print-summary');
      if (canvas && summary) {
        try {
          const dataUrl = canvas.toDataURL('image/png');
          let img = summary.querySelector('.configurator__print-img');
          if (!img) {
            img = document.createElement('img');
            img.className = 'configurator__print-img';
            summary.insertBefore(img, summary.firstChild);
          }
          img.src = dataUrl;
        } catch (e) {}
      }
    };
    const handleAfterPrint = () => {
      const img = document.querySelector('.configurator__print-summary .configurator__print-img');
      if (img) img.remove();
    };
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  const restoredSelections = location.state?.selections;
  const [selections, setSelections] = useState(restoredSelections || {
    hullColor: '#1B3A4B',
    graphicColor: '#2C2C2C',
    top: null,
    engine: null,
    seating: {
      fore_seat: false,
      fore_sunbed: false,
      aft_sunbed: false,
    },
    extras: {
      foreCushion: false,
      foreTableUp: false,
      foreTableDown: false,
      gps: false,
      windshield: false,
      multimedia: false,
      sideRails: false,
      evaDecking: false,
    },
  });

  // Calculate running total
  const totalPrice = useMemo(() => {
    let total = BASE_PRICE;
    if (selections.top && PRICE_DATA.tops[selections.top]) {
      total += PRICE_DATA.tops[selections.top];
    }
    if (selections.engine && PRICE_DATA.engines[selections.engine]) {
      total += PRICE_DATA.engines[selections.engine];
    }
    if (selections.seating) {
      Object.entries(selections.seating).forEach(([key, enabled]) => {
        if (enabled && PRICE_DATA.seating[key]) {
          total += PRICE_DATA.seating[key];
        }
      });
    }
    if (selections.extras) {
      Object.entries(selections.extras).forEach(([key, enabled]) => {
        if (enabled && PRICE_DATA.extras[key]) {
          total += PRICE_DATA.extras[key];
        }
      });
    }
    return total;
  }, [selections]);

  // Check which categories have been configured
  const categoryComplete = useMemo(() => ({
    tops: selections.top !== null,
    engines: selections.engine !== null,
    seating: Object.values(selections.seating).some(Boolean),
    extras: Object.values(selections.extras).some(Boolean),
  }), [selections]);

  const handleSelect = useCallback((key, value) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
    // Don't move camera when selecting options, just show/hide the part
  }, [moveCameraTo, activeCategory]);

  const handleToggleExtra = useCallback((extraKey) => {
    setSelections((prev) => {
      // Handle seating options
      if (extraKey.startsWith('seating_')) {
        const seatingKey = extraKey.replace('seating_', '');
        const willBeEnabled = !prev.seating[seatingKey];
        const newSeating = { ...prev.seating, [seatingKey]: willBeEnabled };
        return { ...prev, seating: newSeating };
      }

      // Handle extras
      const willBeEnabled = !prev.extras[extraKey];

      // Deck Table Up and Deck Table Down are mutually exclusive
      const newExtras = { ...prev.extras, [extraKey]: willBeEnabled };
      if (willBeEnabled && extraKey === 'foreTableUp') {
        newExtras.foreTableDown = false;
      } else if (willBeEnabled && extraKey === 'foreTableDown') {
        newExtras.foreTableUp = false;
      }

      return { ...prev, extras: newExtras };
    });
  }, []);

  const handleCategoryChange = useCallback((catKey, openMobile = false) => {
    setActiveCategory(catKey);
    setPanelOpen(true);
    if (openMobile) setMobilePanelOpen(true);
    moveCameraTo('category', catKey);
  }, [moveCameraTo]);

  const handleNext = useCallback(() => {
    const idx = CATEGORIES.findIndex((c) => c.key === activeCategory);
    if (idx < CATEGORIES.length - 1) {
      handleCategoryChange(CATEGORIES[idx + 1].key);
    } else {
      navigate('/finish-build', { state: { selections, totalPrice } });
    }
  }, [activeCategory, handleCategoryChange, navigate, selections, totalPrice]);

  const handleFinish = useCallback(() => {
    navigate('/finish-build', { state: { selections, totalPrice } });
  }, [navigate, selections, totalPrice]);

  const handleCameraView = useCallback((viewKey) => {
    moveCameraTo('manual', viewKey);
  }, [moveCameraTo]);

  const handleScreenshot = useCallback((dataUrl) => {
    const link = document.createElement('a');
    link.download = 'UNIMAR_STIA580_config.png';
    link.href = dataUrl;
    link.click();
  }, []);

  const currentStepIndex = CATEGORIES.findIndex((c) => c.key === activeCategory);

  return (
    <div className="configurator">
      {/* Coming Soon Overlay */}
      <div className="configurator__coming-soon-overlay">
        <img src="/logo-unimar.webp" alt="UniMar" className="configurator__coming-soon-logo" />
        <h1 className="configurator__coming-soon-title">{t('configurator.comingSoonTitle')}</h1>
        <p className="configurator__coming-soon-text">{t('configurator.comingSoonText')}</p>
        <button className="configurator__coming-soon-btn" onClick={() => navigate('/')}>
          {t('configurator.comingSoonBtn')}
        </button>
      </div>

      {/* Header */}
      <div className="configurator__header">
        <div className="configurator__header-left">
          <span className="configurator__header-model">STIA 580</span>
        </div>
        <div className="configurator__header-center">
          <a href="/" className="configurator__header-logo">
            <img src="/logo-unimar.webp" alt="UniMar" className="configurator__header-logo-img" />
          </a>
        </div>
        <div className="configurator__header-right">
          <button className="configurator__header-action" onClick={() => window.location.href = 'mailto:info@unimar.com'}>Email</button>
        </div>
      </div>


      <div className={`configurator__main ${!panelOpen ? 'configurator__main--panel-closed' : ''}`} style={window.innerWidth <= 1199 ? { height: viewerHeight } : undefined}>
        {/* LEFT COLUMN */}
        <div className="configurator__left">
          {/* 3D Viewer */}
          <div className="configurator__viewer-area">
            <BoatViewer
              selections={selections}
              cameraIntent={cameraIntent}
              />

            {/* Category sidebar icons */}
            <div className="configurator__category-icons">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  className={`configurator__cat-btn ${activeCategory === cat.key ? 'configurator__cat-btn--active' : ''}`}
                  onClick={() => handleCategoryChange(cat.key, true)}
                  title={t(`configurator.categories.${cat.key}`).replace('\n', ' ')}
                >
                  <span className="configurator__cat-icon">
                    <img src={cat.iconSrc} alt={t(`configurator.categories.${cat.key}`).replace('\n', ' ')} style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  </span>
                  <span className="configurator__cat-label">{t(`configurator.categories.${cat.key}`)}</span>
                </button>
              ))}
            </div>

{/* Mobile — open config button */}
            <button className="configurator__mobile-config-btn" onClick={() => setMobilePanelOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 5h12M3 9h12M3 13h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {t('configurator.configure')}
            </button>
          </div>

        </div>

        {/* Print-only summary */}
        <div className="configurator__print-summary">
          <p className="configurator__print-model">STIA 580</p>
          <p className="configurator__print-price">{t('configurator.estimatedTotal')}: {totalPrice.toLocaleString('en-US')} €</p>
        </div>

        {/* RIGHT COLUMN -- Config Panel (desktop) */}
        <div className={`configurator__panel-wrapper ${panelOpen ? 'configurator__panel-wrapper--open' : ''}`}>
          <ConfigPanel
            activeCategory={activeCategory}
            selections={selections}
            onSelect={handleSelect}
            onToggleExtra={handleToggleExtra}
            onClose={() => setPanelOpen(false)}
            onNext={handleNext}
            onFinish={handleFinish}
            totalPrice={totalPrice}
            isLastStep={currentStepIndex === CATEGORIES.length - 1}
          />
        </div>
      </div>

      {/* Mobile fullscreen config panel */}
      <div className={`configurator__mobile-panel ${mobilePanelOpen ? 'configurator__mobile-panel--open' : ''}`}>
        <div className="configurator__mobile-panel-header">
          <span className="configurator__mobile-panel-title">{t('configurator.configureYourBoat')}</span>
          <button className="configurator__mobile-panel-close" onClick={() => setMobilePanelOpen(false)}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2 2L16 16M16 2L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="configurator__mobile-panel-body">
          <ConfigPanel
            activeCategory={activeCategory}
            selections={selections}
            onSelect={handleSelect}
            onToggleExtra={handleToggleExtra}
            onClose={() => setMobilePanelOpen(false)}
            onNext={(e) => { handleNext(e); }}
            onFinish={handleFinish}
            totalPrice={totalPrice}
            isLastStep={currentStepIndex === CATEGORIES.length - 1}
          />
        </div>
      </div>
    </div>
  );
}

export default Configurator;
